import { api } from '../../lib/api';
import type {
  XeroAllocationManagementData,
  XeroAllocationMethod,
  XeroConnectionsStatus,
  XeroContactMappingList,
  XeroRevenueDataset,
  XeroSyncResult,
} from '../../types/xero';

// Client API wrappers for the Xero module (Phase 1). Each module owns its own
// api.ts (the each-module-owns-its-api.ts discipline).

export function getXeroConnections(): Promise<XeroConnectionsStatus> {
  return api<XeroConnectionsStatus>('/api/xero/connections');
}

// Mint a consent URL; the caller navigates the whole tab to it (Xero redirects
// back to /api/xero/callback → /settings/xero).
export function startXeroConnect(): Promise<{ authUrl: string }> {
  return api<{ authUrl: string }>('/api/xero/connect', { method: 'POST' });
}

export function disconnectXeroTenant(tenantId: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/api/xero/connections/${encodeURIComponent(tenantId)}`, {
    method: 'DELETE',
  });
}

// Trigger a sync of one org (pull ACCREC invoices → recompute revenue rollups).
// Runs inline server-side; the response carries the outcome.
export function syncXeroTenant(tenantId: string): Promise<{ result: XeroSyncResult }> {
  return api<{ result: XeroSyncResult }>(
    `/api/xero/connections/${encodeURIComponent(tenantId)}/sync`,
    { method: 'POST' },
  );
}

// ---- Business-lens revenue (chunk 1d) --------------------------------------

export function getXeroRevenue(): Promise<XeroRevenueDataset> {
  return api<XeroRevenueDataset>('/api/xero/revenue');
}

// ---- contact → account mapping (chunk 1c) ----------------------------------

export function getXeroContactMappings(tenantId: string): Promise<XeroContactMappingList> {
  return api<XeroContactMappingList>(`/api/xero/mapping?tenantId=${encodeURIComponent(tenantId)}`);
}

export function setXeroContactMapping(input: {
  tenantId: string;
  contactId: string;
  accountId: string;
}): Promise<{ ok: true }> {
  return api<{ ok: true }>('/api/xero/mapping', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function clearXeroContactMapping(input: {
  tenantId: string;
  contactId: string;
}): Promise<{ ok: true }> {
  return api<{ ok: true }>('/api/xero/mapping', {
    method: 'DELETE',
    body: JSON.stringify(input),
  });
}

// ---- grouped-invoice allocation review + management (Phase 1c) --------------

export function getXeroAllocations(tenantId: string): Promise<XeroAllocationManagementData> {
  return api<XeroAllocationManagementData>(
    `/api/xero/allocations?tenantId=${encodeURIComponent(tenantId)}`,
  );
}

// Confirm / edit a split — the explicit target allocation (accountId + shareBps,
// sum 10000) + its method. "Keep on group" is a group-only rule; deleting a rule
// (below) sends the line back to pending on the next recompute.
export function confirmXeroAllocation(input: {
  tenantId: string;
  contactId: string;
  signature: string;
  descriptionSample: string;
  method: XeroAllocationMethod;
  targets: { accountId: string; shareBps: number }[];
}): Promise<{ ok: true }> {
  return api<{ ok: true }>('/api/xero/allocations/confirm', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function deleteXeroAllocationRule(input: {
  tenantId: string;
  contactId: string;
  signature: string;
}): Promise<{ ok: true }> {
  return api<{ ok: true }>('/api/xero/allocations/rule', {
    method: 'DELETE',
    body: JSON.stringify(input),
  });
}
